import React from "react";
import {
  BodypartType,
  MovementPatternType,
  ProgramTemplateCategory,
  ComponentType,
} from "@growth-machine-llc/stridist-api-client";
import { LibraryFilter } from "./LibraryFilter";
import { mapFilters } from "./filterUtils";

export type SupportedFilterType =
  | BodypartType
  | MovementPatternType
  | ProgramTemplateCategory
  | ComponentType;

interface GenericLibraryContextFilterProps<T extends SupportedFilterType> {
  enumType: Record<string, T>;
  filterTypesLabels: Record<T, string>;
  labelType: string;
  contextFilters?: T[];
  onChange?: (selected: T[]) => void;
  className?: string;

  excludeValues?: T[];
  noneOptionVisible?: boolean;
}

export function GenericLibraryContextFilter<T extends SupportedFilterType>({
  enumType,
  filterTypesLabels,
  labelType,
  contextFilters,
  onChange,
  className,
  excludeValues = [],
  noneOptionVisible = true,
}: GenericLibraryContextFilterProps<T>) {
  const filters = React.useMemo(() => {
    const base = mapFilters(enumType, contextFilters);

    if (!excludeValues.length) return base;

    const filtered = { ...base };
    for (const key of excludeValues) {
      delete filtered[key];
    }
    return filtered;
  }, [enumType, contextFilters, excludeValues]);

  const extractSelected = React.useCallback(
    (filters: Record<T, boolean>): T[] =>
      Object.entries(filters)
        .filter(([, checked]) => checked)
        .map(([key]) => key as T),
    [],
  );

  const handleChange = React.useCallback(
    (newFilters: Record<T, boolean>) => {
      onChange?.(extractSelected(newFilters));
    },
    [extractSelected, onChange],
  );

  return (
    <LibraryFilter
      filters={filters}
      onChange={handleChange}
      filterTypesLabels={filterTypesLabels}
      labelType={labelType}
      className={className}
      noneOptionVisible={noneOptionVisible}
    />
  );
}
